import {
	Component,
	Input,
	ElementRef,
	ViewChild,
	HostListener,
	Renderer2,
	EventEmitter,
	Output,
	OnChanges
} from '@angular/core';

@Component({
	selector: 'cv-dropdown',
	templateUrl: './dropdown.component.html',
	styleUrls: ['./dropdown.component.scss']
})
export class DropdownComponent implements OnChanges {
	isOpen: boolean;
	selectedOptionText: string;
	selectedOption: number;
	/**
	 * If true determines if dropdown should be required and applies style to it when empty.
	 */
	@Input() required: boolean;
	/**
	 * The label of the dropdown input.
	 */
	@Input() label: string;

	/**
	 * @public If `true`, the user cannot interact with the input.
	 */
	@Input() disabled: boolean;

	/**
	 * @public property that receives if the element value is valid
	 */
	@Input() valid = true;

	@Input() icon: string;
	/**
	 * @public set placeholder value by default is 'Select an option'.
	 */
	@Input() placeholder: string;

	/**
	 * @public clear the current selectetion from outside param'.
	 */
	@Input() clearSelection: boolean;

	/**
	 * @public property that recieves the list of items to display
	 */
	@Input() options: Array<any>;

	/**
	 * @public property that recieves the option text to display base on the option value
	 */
	@Input() optionText: string;

	/**
	 * @public property that defines the property to search into the items of the options
	 */
	@Input() optionValue: string;

	/**
	 * @public property that recieves the option to be selected by default
	 */
	@Input() selectedValue: any;

	/**
	 * @public property set the width of the dropdown by default is '100%'
	 */
	@Input() width: string;

	/**
	 * @public propertie that emit an event when an item is selected
	 */
	@Output() valuesChange: EventEmitter<any> = new EventEmitter<any>();

	/**
     * @public propertie that emit an event when the menu of options is diplayed
     */
	@Output() openMenu: EventEmitter<any> = new EventEmitter<any>();

	/**
     * @public propertie that emit an event when the menu of options is closed
     */
	@Output() closeMenu: EventEmitter<any> = new EventEmitter<any>();
	@ViewChild('dropdownButton', { static: true }) dropdownButton: ElementRef<any>;
	@ViewChild('dropdownMenu', { static: true }) dropdownMenu: ElementRef<any>;

	constructor(private renderer: Renderer2) {
		this.options = [];
		this.placeholder = 'Select an option';
		this.width = '100%';
		this.isOpen = false;
	}

	/**
	 * Closes the dropdown menu is user clicks outside.
	 * @param $event: any
	 */
	@HostListener('document:click', ['$event'])
	onClickOutside($event: any): void {
		if (!$event.target.classList.contains('cv-test')) {
			if (!this.dropdownButton.nativeElement.contains($event.target) &&
				!this.dropdownMenu.nativeElement.contains($event.target) &&
				this.isOpen) {
				this.toggleMenu(true);
			}
		}
	}

	/**
	 * Emits the valuesChange event.
	 * @param item value to propagate
	 */
	onValuesChange(item: any): void {
		this.valuesChange.emit(item);
	}

	/**
	 * Toggles(Display or hide) the dropdown menu. If it's display action it calculates the position based
	 * on the button position.
	 * @param forceClose: if true then closes the dropdown menu.
	 */
	toggleMenu(forceClose: boolean = false): void {
		this.isOpen = forceClose ? false : !this.isOpen;
		if (this.isOpen) {
			const top = this.dropdownButton.nativeElement.offsetTop + this.dropdownButton.nativeElement.offsetHeight;
			const left = this.dropdownButton.nativeElement.offsetLeft;
			this.renderer.setStyle(this.dropdownMenu.nativeElement, 'top', `${top}px`);
			this.renderer.setStyle(this.dropdownMenu.nativeElement, 'left', `${left}px`);
			this.renderer.setStyle(this.dropdownMenu.nativeElement, 'display', 'block');
			this.openMenu.emit();
		} else {
			this.renderer.setStyle(this.dropdownMenu.nativeElement, 'display', 'none');
			this.closeMenu.emit();
		}
	}

	/**
	 * select and set the current clicked option
	 * @param item item selected
	 * @param index index selected
	 */
	selectOption(item: any, index: number): void {
		this.selectedOption = index;
		this.selectedOptionText = item[this.optionText] || item;
		this.toggleMenu(this.isOpen);
		this.onValuesChange(item);
	}

	/**
	 * checks  the current selected option
	 * @param index index selected
	 */
	isOptionSelected(index: number): boolean {
		return this.selectedOption === index;
	}

	/**
     * Clear the current selected option and emits and empty array
     */
	clearValues($event: MouseEvent = null): void {
		if ($event) {
			$event.stopPropagation();
		}
		this.selectedOptionText = null;
		this.selectedOption = null;
		this.onValuesChange(null);
	}

	/**
	 * doc must exist
	 */
	ngOnChanges(): void {
		let index = null;

		if (this.optionText && this.options && this.selectedValue) {
			const value = this.optionValue ? this.optionValue : this.optionText;

			index = this.options.findIndex(
				o => o[value] === this.selectedValue[value]
			);
			if (Number.isInteger(index) && index >= 0) {
				this.selectedOption = index;
				this.selectedOptionText = this.options[index][this.optionText];
			}
		} else if (this.options && this.selectedValue) {
			index = this.options.findIndex(o => o === this.selectedValue);
			if (Number.isInteger(index) && index >= 0) {
				this.selectedOption = index;
				this.selectedOptionText = this.selectedValue;
			}
		}
		if (this.clearSelection) {
			this.clearValues();
		}
	}

	/**
	 * Returns true if component value is valid
	 */
	isValid(): boolean {
		if (this.required) {
			return this.selectedOptionText !== undefined
				&& this.selectedOptionText !== null
				&& this.selectedOptionText.length > 0;
		}
		return true;
	}
}
